<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="800px"
    class="instagram-scenario-editor-dialog"
  >
    <div slot="title">{{ 'シナリオ編集' }}</div>

    <div v-loading="loading" class="dialog-content">
      <!-- 4ステップの進行インジケータ -->
      <div class="scenario-step-indicator">
        <Steps :active="currentStep - 1" :options="stepLabels" />
      </div>

      <!-- コンテンツエリア -->
      <div class="scenario-editor-content">
        <!-- ステップ1: フォローチェック -->
        <StepFollowCheck v-if="currentStep === 1" />

        <!-- ステップ2: メッセージ -->
        <StepMessage v-if="currentStep === 2" />

        <!-- ステップ3: 抽選中 -->
        <StepLottery v-if="currentStep === 3" />

        <!-- ステップ4: 当落メッセージ -->
        <StepResult v-if="currentStep === 4" />
      </div>
    </div>

    <!-- フッターボタン -->
    <div slot="footer" class="dialog-footer">
      <div>
        <!-- 戻るボタン（ステップ2以降で表示） -->
        <el-button v-if="currentStep > 1" @click="handleBack">
          <Icon name="arrow-left" space="right" />
          {{ '戻る' }}
        </el-button>
      </div>

      <div class="footer-right">
        <!-- キャンセルボタン（全ステップで表示） -->
        <el-button @click="handleCancel">
          {{ 'キャンセル' }}
        </el-button>

        <!-- 次へボタン（ステップ1-3で表示） -->
        <el-button
          v-if="currentStep < 4"
          type="primary"
          @click="handleNext"
          :disabled="loading || hasCurrentStepErrors"
        >
          {{ '次へ' }}
          <Icon name="arrow-right" space="left" />
        </el-button>

        <!-- 保存ボタン（ステップ4のみ表示） -->
        <el-button
          v-if="currentStep === 4"
          type="primary"
          @click="handleSave"
          :disabled="loading || !store.isStep4Valid"
        >
          <i class="mdi mdi-content-save" />
          {{ '保存' }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { storeToRefs } from 'pinia'
import { getCurrentInstance, ref, toRefs, watch } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Steps from '@/client/components/_atoms/Steps.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import { useInstagramScenarioEditorStore } from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'

import StepFollowCheck from './StepFollowCheck.vue'
import StepLottery from './StepLottery.vue'
import StepMessage from './StepMessage.vue'
import StepResult from './StepResult.vue'

const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

interface Emits {
  (e: 'close'): void
  (e: 'saved'): void
}

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  prizeId: {
    type: Number,
    default: null
  },
  campaignStatus: {
    type: Number,
    default: null
  }
})

// Destructure props for template use
const { visible, prizeId, campaignStatus } = toRefs(props)

const emit = defineEmits<Emits>()

// Store
const store = useInstagramScenarioEditorStore()
const { currentStep, hasCurrentStepErrors } = storeToRefs(store)

// State
const loading = ref(false)

// ステップラベル
// デザイン仕様: 「フォローチェック」は「フォロー」+改行、「当落メッセージ」は「当落」+改行。
// Element UI の el-step は title をテキストとして描画するため、改行制御は '\n'+ white-space: pre-line でする。
const stepLabels = ['フォロー\nチェック', 'メッセージ', '抽選中', '当落\nメッセージ']

// Methods
const fetchScenarioData = async () => {
  if (!prizeId.value) {
    return
  }

  try {
    loading.value = true
    await store.open(prizeId.value)
  } catch {
    proxy.$notify({
      title: 'シナリオデータの読み込みに失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  } finally {
    loading.value = false
  }
}

const isCampaignRunning = (status: number | null): boolean => {
  if (status === null) {
    return false
  }
  return [
    CAMPAIGN_STATUS.BEFORE_COLLECTION,
    CAMPAIGN_STATUS.DURING_COLLECTION,
    CAMPAIGN_STATUS.DURING_FINAL_COLLECTION,
    CAMPAIGN_STATUS.COLLECTION_COMPLETED
  ].includes(status)
}

const handleClose = () => {
  // 未保存の変更がある場合、確認ダイアログを表示
  if (store.hasUnsavedChanges) {
    const confirmClose = window.confirm('編集内容が保存されていません。ダイアログを閉じますか？')
    if (!confirmClose) return

    // 変更を破棄してダイアログを閉じる
    store.close()
    emit('close')
  } else {
    // 変更がない場合、そのまま閉じる
    store.close()
    emit('close')
  }
}

const handleNext = () => {
  // バリデーション実行
  const isValid = store.validateCurrentStep()

  // バリデーション成功時のみ次のステップへ遷移
  if (isValid) {
    store.goToNextStep()
  }
}

const handleBack = () => {
  // 前のステップに戻る（バリデーションなし）
  store.goToPreviousStep()
}

const handleSave = async () => {
  // バリデーション実行
  const isValid = store.validateCurrentStep()

  if (!isValid) {
    return
  }

  try {
    loading.value = true
    await store.save()

    proxy.$notify({
      title: 'シナリオを保存しました。',
      customClass: 'success',
      duration: 5000
    })

    emit('saved')
    emit('close')
  } catch {
    proxy.$notify({
      title: 'シナリオの保存に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  // キャンセルボタンも同じ確認ロジックを使用
  handleClose()
}

// Watch
watch(visible, newValue => {
  if (newValue && prizeId.value) {
    store.setCampaignRunning(isCampaignRunning(campaignStatus.value))
    fetchScenarioData()
  }
})

watch(campaignStatus, newValue => {
  if (visible.value) {
    store.setCampaignRunning(isCampaignRunning(newValue))
  }
})

// Expose public methods
defineExpose({
  open: fetchScenarioData,
  close: handleClose
})
</script>

<style scoped lang="scss">
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog) {
  .el-dialog__header {
    padding: $spacing-unit * 2;
    font-size: 16px;
    color: $text-color-white !important;
    background-color: $background-dark-color;
  }

  .el-dialog__body {
    padding: $spacing-unit * 2;
    padding-bottom: 0;
  }

  .el-dialog__footer {
    padding: 0 $spacing-unit * 2 $spacing-unit * 2;
  }

  .el-dialog__title,
  .el-dialog__headerbtn .el-dialog__close {
    color: $text-color-white;
  }
}

.instagram-scenario-editor-dialog {
  .scenario-step-indicator {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: $spacing-unit * 3;

    /* このダイアログ内だけ Steps の幅を144pxで統一し中央寄せする */
    :deep(.steps) {
      display: inline-flex;
    }

    :deep(.steps .el-step) {
      /* 各ステップ幅を統一 */
      flex: 0 0 144px !important;
      width: 144px !important;
    }

    :deep(.steps .el-step.is-simple:not(:last-of-type) .el-step__title) {
      max-width: 100%;
    }

    :deep(.steps .el-step__title) {
      box-sizing: border-box;
      display: flex; /* 複数行を中央揃え */
      align-items: center;
      justify-content: center;
      height: 48px; /* 擬似要素（矢羽）高さと揃える */
      padding: $spacing-unit * 2 ($spacing-unit * 4 - 12px) $spacing-unit * 2 $spacing-unit * 4; /* 元コンポーネントと同じ計算式を再適用 */
      margin: 0;
      line-height: 1.3;
      text-align: center;

      /* \n を改行として解釈させる */
      white-space: pre-line;
    }
  }

  .scenario-editor-content {
    min-height: 200px;
  }

  .dialog-footer {
    display: flex;
    justify-content: space-between;

    .footer-right {
      display: flex;

      .el-button:not(:first-child) {
        margin-left: $spacing-unit;
      }
    }
  }
}
</style>
